import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { FormCheckbox } from '../../../component/form/FormCheckbox';
import { FormDatePicker } from '../../../component/form/FormDatePicker';
import { FormIntegerNumberInput } from '../../../component/form/FormIntegerNumberInput';
import { FormNumberInput } from '../../../component/form/FormNumberInput';
import { FormSelectionCard } from '../../../component/form/FormSelectionCard';
import { FormTextField } from '../../../component/form/FormTextField';
import { buildDefaultValues, buildFormSchema, FormValue, toSubmitValues } from './buildFormSchema';
import { FieldRule, FormAction, FormBlock, ReadonlyBlock } from './formDescriptor.types';

interface Props {
  blocks: FormBlock[];
  actions: FormAction[];
  onSubmit: (actionKey: string, values: Record<string, string | boolean | number | null>) => void;
  busy?: boolean;
}

const isRequired = (rules?: FieldRule[]) => !!rules?.some((r) => r.kind === 'required');

const spansFullWidth = (block: FormBlock) =>
  block.type === 'readonly' ||
  block.type === 'choice' ||
  block.type === 'confirm' ||
  (block.type === 'text' && !!block.multiline);

// Not passed as helperText: the POS form components render their error message there.
function WithHint({ hint, children }: { hint?: string | null; children: JSX.Element }) {
  if (!hint) return children;
  return (
    <Box>
      {children}
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    </Box>
  );
}

function ReadonlyRows({ block }: { block: ReadonlyBlock }) {
  return (
    <Box>
      {block.label && (
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {block.label}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 3, rowGap: 0.5 }}>
        {block.rows.map(([key, value]) => (
          <Box key={key} sx={{ display: 'contents' }}>
            <Typography color="text.secondary">{key}</Typography>
            <Typography fontWeight={500}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function FormRenderer({ blocks, actions, onSubmit, busy }: Props) {
  const { control, handleSubmit } = useForm<Record<string, FormValue>>({
    resolver: zodResolver(buildFormSchema(blocks)),
    defaultValues: buildDefaultValues(blocks),
  });

  const submitWith = (action: FormAction) => (event: FormEvent) => {
    event.preventDefault();
    handleSubmit((values) => onSubmit(action.key, toSubmitValues(blocks, values)))(event);
  };

  const renderBlock = (block: FormBlock, index: number) => {
    switch (block.type) {
      case 'readonly':
        return <ReadonlyRows key={index} block={block} />;

      case 'choice':
        return (
          <FormSelectionCard
            key={block.field}
            control={control}
            name={block.field}
            label={block.label}
            required={isRequired(block.rules)}
            options={block.options}
          />
        );

      case 'text':
        return (
          <WithHint key={block.field} hint={block.hint}>
            <FormTextField
              control={control}
              name={block.field}
              label={block.label}
              required={isRequired(block.rules)}
              placeholder={block.placeholder ?? undefined}
              multiline={block.multiline}
              minRows={block.multiline ? 3 : undefined}
            />
          </WithHint>
        );

      case 'number': {
        const NumberField = block.decimals === 0 ? FormIntegerNumberInput : FormNumberInput;
        return (
          <WithHint key={block.field} hint={block.hint}>
            <NumberField
              control={control}
              name={block.field}
              label={block.label}
              required={isRequired(block.rules)}
              placeholder={block.placeholder ?? undefined}
              endAdornment={block.suffix ? <span>{block.suffix}</span> : undefined}
            />
          </WithHint>
        );
      }

      case 'date':
        return (
          <WithHint key={block.field} hint={block.hint}>
            <FormDatePicker
              control={control}
              name={block.field}
              label={block.label}
              required={isRequired(block.rules)}
            />
          </WithHint>
        );

      case 'confirm':
        return (
          <FormCheckbox
            key={block.field}
            control={control}
            name={block.field}
            label={block.label}
          />
        );

      default:
        return (
          <Alert key={index} severity="info" data-testid="unknown-block">
            Dieser Schritt enthält eine Eingabe, die diese Dekato-Version noch nicht darstellen
            kann. Bitte im Backoffice melden.
          </Alert>
        );
    }
  };

  return (
    <Stack gap={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 3,
          rowGap: 3,
          alignItems: 'start',
        }}
      >
        {blocks.map((block, index) => (
          <Box
            key={'field' in block ? block.field : index}
            sx={{ gridColumn: spansFullWidth(block) ? '1 / -1' : 'auto', minWidth: 0 }}
          >
            {renderBlock(block, index)}
          </Box>
        ))}
      </Box>

      <Stack direction="row" gap={2} justifyContent="flex-end" flexWrap="wrap">
        {actions.map((action) => {
          const button = (
            <span key={action.key}>
              <Button
                variant={action.primary ? 'contained' : 'outlined'}
                color={action.primary ? 'primary' : 'secondary'}
                disabled={!action.allowed || busy}
                onClick={submitWith(action)}
              >
                {action.label}
              </Button>
            </span>
          );

          return action.allowed ? (
            button
          ) : (
            <Tooltip key={action.key} title="Dafür fehlt Ihnen die Berechtigung.">
              {button}
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
}
